import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary, FallbackProps } from 'react-error-boundary';
import { usePrevious } from 'react-use';

import { useTranslate } from '@growtherapy/i18n';
import { Alert, DRAWER_TITLE_ID, DrawerBody, IconButton, tailwindMerge } from '@growtherapy/sprout-ui';

import { logger } from '../datadog/logger';
import { useTheme } from '../hooks';
import { Theme } from '../state';

function FallbackComponent({ error }: FallbackProps) {
  const t = useTranslate();
  useEffect(
    function logError() {
      logger.error('The push drawer crashed after encountering an unexpected error; displaying fallback', error);
    },
    [error],
  );

  return (
    <DrawerBody>
      <Alert
        className='mt-12'
        heading={t('telehealth-core:errorsCore.somethingWentWrongPleaseTryAgainLater')}
        use='error'
        headingAs='h2'
      />
    </DrawerBody>
  );
}

function ErrorBoundary({ children }: { children: ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={FallbackComponent}>{children}</BaseErrorBoundary>;
}

export type PushDrawerProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: 'left' | 'right';
  className?: string;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
};

export function PushDrawer({
  id,
  isOpen = true,
  onClose,
  children,
  placement = 'right',
  className,
  closeButtonRef: closeButtonRefProp,
}: PushDrawerProps) {
  const t = useTranslate();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const prevChildren = usePrevious(children);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(isOpen);
    }
  }, [isOpen]);

  if (!shouldRender) {
    return <></>;
  }

  return (
    <aside
      aria-labelledby={DRAWER_TITLE_ID}
      className={tailwindMerge(
        classNames(
          'shrink border border-neutral-800 bg-neutral-300 flex flex-col', // general styling regardless of screen size
          'max-sm:z-20 max-sm:fixed top-0 bottom-0 max-h-full h-screen w-screen', // mobile styling
          'sm:top-3 sm:bottom-3 sm:rounded-xl sm:h-full sm:max-w-[42rem]', // larger than mobile styling
          {
            'animate-slide-up sm:animate-slide-in-right right-0 sm:right-3': isOpen && placement === 'right',
            'animate-slide-down sm:animate-slide-out-left': !isOpen && placement === 'right',
            'animate-slide-up sm:animate-slide-in-left left-3': isOpen && placement === 'left',
            'animate-slide-down sm:animate-slide-out-right': !isOpen && placement === 'left',
            'transition-all max-sm:opacity-5 sm:w-0': !isOpen,
          },
          className,
        ),
      )}
      id={id}
      onAnimationEnd={(): void => {
        if (!isOpen) {
          setShouldRender(false);
        } else {
          closeButtonRefProp.current?.focus();
        }
      }}
    >
      <IconButton
        ref={closeButtonRefProp}
        aria-label={t('telehealth-core:chatDrawer.closeDrawer')}
        iconDefinition={faXmark}
        onClick={onClose}
        className={classNames(
          'absolute right-0 my-4 mx-6 h-8 w-8 flex-none items-center justify-center sm:my-6 sm:mx-8 md:mx-10 md:my-8',
          'rebrand:h-component-125 rebrand:w-component-125 rebrand:md:mx-450 rebrand:md:my-450',
          {
            hidden: !isOpen,
          },
        )}
      />
      <ErrorBoundary>{isOpen ? children : prevChildren}</ErrorBoundary>
    </aside>
  );
}

export function PushDrawerContainer({ children, className }: Pick<PushDrawerProps, 'children' | 'className'>) {
  const { theme } = useTheme();
  return (
    <div
      className={classNames(
        { 'border-t border-b': theme === Theme.LIGHT },
        'flex flex-row gap-2 overflow-hidden h-full w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}
